import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Contact, Plus } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { AdminOrgPicker } from '@apps/web/components/admin/admin_org_picker';
import { CareCoordinatorFormModal } from '@apps/web/components/admin/care_coordinator_form_modal';
import {
    AdminBadge,
    AdminStatusDot,
} from '@apps/web/features/admin_design/admin_badge';
import {
    AdminButton,
    AdminSearchInput,
} from '@apps/web/features/admin_design/admin_controls';
import {
    AdminPage,
    AdminPageBody,
    AdminPageHeader,
} from '@apps/web/features/admin_design/admin_shell';
import { AdminTableSkeleton } from '@apps/web/features/admin_design/admin_skeleton';
import {
    AdminEmptyState,
    AdminErrorState,
} from '@apps/web/features/admin_design/admin_states';
import {
    AdminTable,
    AdminTableBody,
    AdminTableCell,
    AdminTableHeadCell,
    AdminTableHeader,
    AdminTableRow,
} from '@apps/web/features/admin_design/admin_table';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import { useAdminCareCoordinators } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type AdminCareCoordinatorListResponse } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/care-coordinators/')({
    head: () => ({
        meta: [
            {
                title: 'Care Coordinators | Phoebe Admin',
            },
        ],
    }),
    validateSearch: z.object({
        organizationId: z.string().optional(),
    }),
    component: AdminCareCoordinatorsPage,
});

const CARE_COORDINATOR_TABLE_COLUMNS = [
    'Name',
    'Organization',
    'Email',
    'Phone',
    'Linked User',
    'Status',
] as const;

function AdminCareCoordinatorsPage() {
    const router = useRouter();
    const { organizationId } = Route.useSearch();
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const {
        data: coordinatorsResponse,
        isLoading,
        error,
        hasRequestFailed,
        mutate: mutateCoordinators,
    } = useAdminCareCoordinators({
        page: 1,
        per_page: 100,
        ...(organizationId && { organization_id: organizationId }),
        ...(searchQuery && { search: searchQuery }),
    }) as {
        data: AdminCareCoordinatorListResponse | undefined;
        isLoading: boolean;
        error: Error | undefined;
        hasRequestFailed: boolean;
        mutate: () => void;
    };

    const coordinators = coordinatorsResponse?.care_coordinators ?? [];
    const hasLoadError =
        error !== undefined ||
        (hasRequestFailed && coordinatorsResponse === undefined);
    const getCoordinatorNavigation = (coordinatorId: string) => {
        const routeOptions = {
            to: '/admin/care-coordinators/$care-coordinator-id',
            params: { 'care-coordinator-id': coordinatorId },
        } as const;
        return {
            href: router.buildLocation(routeOptions).href,
            navigate: () => {
                void router.navigate(routeOptions);
            },
        };
    };

    return (
        <AdminPage>
            <AdminPageHeader
                title="Care Coordinators"
                meta={`${coordinatorsResponse?.total ?? 0} ${
                    organizationId ? 'in organization' : 'total'
                }`}
                actions={
                    <AdminButton
                        onClick={() => setIsCreateModalOpen(true)}
                        variant="primary"
                    >
                        <Plus strokeWidth={1.75} />
                        Create
                    </AdminButton>
                }
            >
                <AdminSearchInput
                    placeholder="Search care coordinators…"
                    containerClassName="max-w-md"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="flex items-center gap-2">
                    <span className="text-admin-sm text-admin-ink-muted font-medium">
                        Organization:
                    </span>
                    <AdminOrgPicker
                        triggerClassName="rounded-admin-sm border-admin-border text-admin-base h-8 w-[250px]"
                        value={organizationId}
                        onValueChange={(id) => {
                            void router.navigate({
                                to: '/admin/care-coordinators',
                                search: (prev) => ({
                                    ...prev,
                                    organizationId: id,
                                }),
                            });
                        }}
                    />
                </div>
            </AdminPageHeader>
            <AdminPageBody>
                {isLoading ? (
                    <AdminTableSkeleton
                        columns={CARE_COORDINATOR_TABLE_COLUMNS}
                    />
                ) : hasLoadError ? (
                    <AdminErrorState
                        title="Failed to load care coordinators"
                        description="Please try again."
                        onRetry={() => {
                            mutateCoordinators();
                        }}
                    />
                ) : coordinators?.length === 0 ? (
                    <AdminEmptyState
                        icon={Contact}
                        title={
                            searchQuery
                                ? 'No care coordinators found'
                                : 'No care coordinators yet'
                        }
                        description={
                            searchQuery
                                ? 'Try a different name or clear the search.'
                                : 'Care coordinators appear here once an organization syncs.'
                        }
                    />
                ) : (
                    <AdminTable>
                        <AdminTableHeader>
                            <tr>
                                {CARE_COORDINATOR_TABLE_COLUMNS.map(
                                    (column) => (
                                        <AdminTableHeadCell key={column}>
                                            {column}
                                        </AdminTableHeadCell>
                                    ),
                                )}
                            </tr>
                        </AdminTableHeader>
                        <AdminTableBody>
                            {coordinators.map((coordinator) => (
                                <AdminTableRow
                                    key={coordinator.id}
                                    className="cursor-pointer"
                                    {...getNavigableElementProps(
                                        getCoordinatorNavigation(
                                            coordinator.id,
                                        ),
                                    )}
                                >
                                    <AdminTableCell className="text-admin-ink font-medium">
                                        {coordinator.first_name}{' '}
                                        {coordinator.last_name}
                                        {coordinator.preferred_name && (
                                            <span className="text-admin-ink-muted ml-1 font-normal">
                                                ({coordinator.preferred_name})
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {coordinator.internal_organization_name?.trim() ||
                                            coordinator.organization_name}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {coordinator.email || (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {coordinator.phone_number || (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {coordinator.linked_user_email ? (
                                            <AdminBadge tone="outline">
                                                {coordinator.linked_user_email}
                                            </AdminBadge>
                                        ) : (
                                            <span className="text-admin-ink-faint">
                                                Not linked
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        <div className="flex items-center gap-2">
                                            <AdminStatusDot
                                                tone={
                                                    coordinator.is_active
                                                        ? 'ok'
                                                        : 'neutral'
                                                }
                                                label={
                                                    coordinator.is_active
                                                        ? 'Active'
                                                        : 'Inactive'
                                                }
                                            />
                                            {!coordinator.include_in_variable && (
                                                <AdminBadge tone="outline">
                                                    Excluded
                                                </AdminBadge>
                                            )}
                                        </div>
                                    </AdminTableCell>
                                </AdminTableRow>
                            ))}
                        </AdminTableBody>
                    </AdminTable>
                )}
            </AdminPageBody>
            <CareCoordinatorFormModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                mode="create"
                onSaveSuccess={() => mutateCoordinators()}
            />
        </AdminPage>
    );
}
